/**
 * Payment Cancelled Page
 * 
 * This page is shown when user cancels Stripe Checkout in a new tab.
 * It automatically closes itself so the user returns to the original page.
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XCircle, Loader2 } from 'lucide-react';

export function PaymentCancelledPage() {
  const { t } = useTranslation();
  const [closing, setClosing] = useState(false);
  const [canClose, setCanClose] = useState(true);

  useEffect(() => {
    // Try to close the window after a short delay
    const timer = setTimeout(() => {
      setClosing(true);
      
      // Attempt to close the window
      try {
        window.close();
        
        // If window.close() didn't work, show a message
        setTimeout(() => {
          setCanClose(false);
        }, 500);
      } catch {
        setCanClose(false);
      }
    }, 1500);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="payment-cancelled-card max-w-md w-full text-center space-y-6">
        {/* Cancelled Icon */}
        <div className="flex justify-center">
          <div className="w-20 h-20 rounded-full bg-muted flex items-center justify-center">
            <XCircle className="h-12 w-12 text-muted-foreground" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-foreground">
          {t('payment.paymentCancelled', 'Payment Cancelled')}
        </h1>

        {/* Message */}
        <div className="space-y-2">
          {closing && canClose ? (
            <div className="flex items-center justify-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>{t('payment.closingWindow', 'Closing this window...')}</span>
            </div>
          ) : !canClose ? (
            <p className="text-muted-foreground">
              {t('payment.closeTabManually', 'You can now close this tab and return to the previous page.')}
            </p>
          ) : (
            <p className="text-muted-foreground">
              {t('payment.paymentWasCancelled', 'Your payment was cancelled. No charges were made.')}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

